import { useState, useEffect } from "react";
import { db } from "../firebase";
import { 
  collection, 
  getDocs, 
  doc, 
  setDoc, 
  deleteDoc, 
  serverTimestamp 
} from "firebase/firestore";
import { Pencil, Trash2, Plus, X, Upload } from "lucide-react";

export default function EditionsAdmin() {
  const [editions, setEditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEdition, setEditingEdition] = useState(null);
  
  // Form states
  const [form, setForm] = useState({
    id: "",
    name: "",
    about: "",
    date: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [singleCoverFile, setSingleCoverFile] = useState(null);
  const [singleCoverPreview, setSingleCoverPreview] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formLoading, setFormLoading] = useState(false);

  // Fetch all editions
  const fetchEditions = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, "editions"));
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setEditions(data);
    } catch (err) {
      console.error("Error fetching editions:", err);
      alert("Failed to load editions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEditions();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this edition?")) return;
    
    setLoading(true);
    try {
      const docRef = doc(db, "editions", id);
      await deleteDoc(docRef);
      
      setEditions(prevEditions => prevEditions.filter(e => e.id !== id));
      alert("✅ Edition deleted successfully!");
    } catch (err) {
      console.error("Delete error:", err);
      alert(`❌ Failed to delete: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const openAddForm = () => {
    setEditingEdition(null);
    setForm({ id: "", name: "", about: "", date: "" });
    setPreview("");
    setSingleCoverPreview("");
    setImageFile(null);
    setSingleCoverFile(null);
    setUploadProgress(0);
    setShowForm(true);
  };

  const openEditForm = (edition) => {
    setEditingEdition(edition);
    setForm({
      id: edition.id,
      name: edition.name || "",
      about: edition.about || "",
      date: edition.date || "",
    });
    setPreview(edition.coverpage || "");
    setSingleCoverPreview(edition.singlecover || "");
    setImageFile(null);
    setSingleCoverFile(null);
    setUploadProgress(0);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingEdition(null);
    setForm({ id: "", name: "", about: "", date: "" });
    setPreview("");
    setSingleCoverPreview("");
    setImageFile(null);
    setSingleCoverFile(null);
    setUploadProgress(0);
  };

  const uploadImage = (file) => {
    if (!file) return Promise.resolve(null);

    return new Promise((resolve, reject) => {
      const data = new FormData();
      data.append("file", file);
      data.append("upload_preset", "guideimages");
      data.append("cloud_name", "dkevvbzvn");

      const xhr = new XMLHttpRequest();
      xhr.open(
        "POST",
        "https://api.cloudinary.com/v1_1/dkevvbzvn/image/upload"
      );

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          setUploadProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          const res = JSON.parse(xhr.responseText);
          resolve(res.secure_url);
        } else {
          reject(new Error("Image upload failed"));
        }
      };

      xhr.onerror = () => reject(new Error("Network error"));
      xhr.send(data);
    });
  };

  const handleSubmit = async () => {
    if (!form.id || !form.name) {
      alert("ID and Name are required");
      return;
    }

    setFormLoading(true);
    try {
      const coverImageUrl = imageFile ? await uploadImage(imageFile) : preview;
      const singleCoverUrl = singleCoverFile ? await uploadImage(singleCoverFile) : singleCoverPreview;
      
      await setDoc(doc(db, "editions", form.id), {
        name: form.name,
        about: form.about,
        date: form.date,
        coverpage: coverImageUrl || null,
        singlecover: singleCoverUrl || null,
        createdAt: editingEdition?.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      alert("✅ Edition saved successfully!");
      closeForm();
      fetchEditions();
    } catch (err) {
      console.error(err);
      alert("❌ Failed to save edition");
    } finally {
      setFormLoading(false);
    }
  };

  if (loading && !showForm) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Loading editions...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Editions Management</h1>
          <button
            onClick={openAddForm}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
          >
            <Plus size={20} />
            Add Edition
          </button>
        </div>

        {/* Form Modal - ALL FORM CODE IS HERE INSIDE EditionsAdmin */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Form Header */}
              <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
                <h2 className="text-2xl font-bold">
                  {editingEdition ? "Edit Edition" : "Add New Edition"}
                </h2>
                <button
                  onClick={closeForm}
                  className="text-gray-500 hover:text-gray-700 p-1 hover:bg-gray-100 rounded"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Form Content */}
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Edition ID *
                  </label>
                  <input
                    type="text"
                    value={form.id}
                    onChange={(e) => setForm({ ...form, id: e.target.value })}
                    placeholder="e.g., sept-2023"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={!!editingEdition}
                  />
                  {editingEdition && (
                    <p className="text-xs text-gray-500 mt-1">ID cannot be changed</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Edition Name *
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g., September 2023 Edition"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Date
                  </label>
                  <input
                    type="text"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    placeholder="e.g., September 2023"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    About
                  </label>
                  <textarea
                    value={form.about}
                    onChange={(e) => setForm({ ...form, about: e.target.value })}
                    placeholder="Brief description of this edition"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Cover Image
                  </label>
                  <div className="flex items-center gap-4">
                    <label className="flex-1 cursor-pointer">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-500 transition text-center">
                        <Upload className="mx-auto mb-2 text-gray-400" size={24} />
                        <span className="text-sm text-gray-600">
                          Click to upload image
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setImageFile(file);
                              setPreview(URL.createObjectURL(file));
                              setUploadProgress(0);
                            }
                          }}
                          className="hidden"
                        />
                      </div>
                    </label>
                    
                    {preview && (
                      <img
                        src={preview}
                        alt="Preview"
                        className="w-32 h-32 object-cover rounded-lg border"
                      />
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Single Cover Image
                  </label>
                  <div className="flex items-center gap-4">
                    <label className="flex-1 cursor-pointer">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-500 transition text-center">
                        <Upload className="mx-auto mb-2 text-gray-400" size={24} />
                        <span className="text-sm text-gray-600">
                          Click to upload single cover
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setSingleCoverFile(file);
                              setSingleCoverPreview(URL.createObjectURL(file));
                            }
                          }}
                          className="hidden"
                        />
                      </div>
                    </label>
                    
                    {singleCoverPreview && (
                      <img
                        src={singleCoverPreview}
                        alt="Single Cover Preview"
                        className="w-32 h-32 object-cover rounded-lg border"
                      />
                    )}
                  </div>
                </div>

                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                      <div
                        className="bg-blue-600 h-2 transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      Uploading: {uploadProgress}%
                    </p>
                  </div>
                )}

                {/* Form Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    onClick={closeForm}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={formLoading}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {formLoading ? "Saving..." : "Save Edition"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Cover
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Single Cover
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    About
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {editions.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                      No editions found. Click "Add Edition" to create one.
                    </td>
                  </tr>
                ) : (
                  editions.map((edition) => (
                    <tr key={edition.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        {edition.coverpage ? (
                          <img
                            src={edition.coverpage}
                            alt={edition.name}
                            className="w-16 h-16 object-cover rounded"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">
                            No Image
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {edition.singlecover ? (
                          <img
                            src={edition.singlecover}
                            alt={`${edition.name} single`}
                            className="w-16 h-16 object-cover rounded"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">
                            No Image
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm font-mono text-gray-900">
                        {edition.id}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {edition.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {edition.date || "-"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                        {edition.about || "-"}
                      </td>
                      <td className="px-6 py-4 text-right text-sm">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openEditForm(edition)}
                            className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded"
                            title="Edit"
                          >
                            <Pencil size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(edition.id)}
                            className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}